import React, { useState, useEffect } from 'react'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useWizard } from '@/components/mediaBuying/adCampaign/AdCampaignWizard'
import { GeolocationIcon, CountryIcon, WorldwideIcon } from '@/components/icons'
import { getAvailableStages } from '@/utils/ad-campaigns-helpers'
import Funnel from '@/components/funnel'

const LOCATION_OPTIONS = [
    {
        id: 'geolocation',
        icon: GeolocationIcon,
        title: 'Campaign Geolocation',
        description:
            "Target people within an 80 km radius around your campaign's specific address. Best for local businesses, events, clinics, or stores wanting to attract nearby customers.",
        additionalText:
            'Useful if you only serve clients in a local area or want foot traffic close to your physical location.',
        example: 'Example: A beauty clinic in Sydney wanting to attract customers within driving distance.',
    },
    {
        id: 'countrywide',
        icon: CountryIcon,
        title: 'Countrywide Targeting',
        description:
            'Target people across the entire country where your campaign is based (e.g., all of Australia if your business is in Sydney). Best for e-commerce stores, online services, or franchises that can serve customers anywhere in the country.',
        additionalText:
            'Useful if you ship products or offer services nationally and want broader reach while staying relevant to your country.',
        example: 'Example: An online course provider or an e-commerce business selling supplements across Australia.',
    },
    {
        id: 'worldwide',
        icon: WorldwideIcon,
        title: 'Worldwide Targeting',
        description:
            'Target people worldwide across all countries. Best for global e-commerce brands, digital products, or services with no geographical restrictions.',
        additionalText:
            'Useful if your product or service can be used by anyone, anywhere and you want to scale reach internationally.',
        example: 'Example: A global SaaS tool, downloadable guides, or worldwide e-commerce shipping.',
    },
]

const LocationTargeting = () => {
    const { getStepContentData, setStepContentData, registerNextGuard, setVisible } = useWizard()

    // Initialize state from saved data or default to 'geolocation'
    const [selectedLocation, setSelectedLocation] = useState(() => {
        const savedData = getStepContentData()
        return savedData?.selectedLocation || 'geolocation'
    })

    // Save initial data and whenever selection changes
    useEffect(() => {
        setStepContentData({ selectedLocation })
    }, [selectedLocation, setStepContentData])

    // Register next guard to ensure selection is made
    useEffect(() => {
        const nextGuard = () => {
            return selectedLocation !== null
        }
        registerNextGuard(nextGuard)
    }, [selectedLocation, registerNextGuard])

    const handleLocationChange = (locationId) => {
        setSelectedLocation(locationId)
    }

    // Get selected objective from previous step to determine available stages
    const campaignObjectiveData = getStepContentData('campaignObjective') || {}
    const selectedObjective = campaignObjectiveData.selectedObjective || 'awareness'
    const availableStages = getAvailableStages(selectedObjective)

    return (
        <div className="tw:flex tw:w-full tw:h-full tw:min-h-0">
            <div className="tw:flex-1 tw:pb-6 tw:overflow-y-auto">
                <div className="tw:p-6 tw:max-w-[580px]">
                    <RadioGroup
                        value={selectedLocation}
                        onValueChange={handleLocationChange}
                        className="tw:space-y-4 tw:gap-0"
                    >
                        {LOCATION_OPTIONS.map((option) => {
                            const IconComponent = option.icon
                            return (
                                <div
                                    key={option.id}
                                    className="tw:flex tw:items-start tw:gap-4 tw:rounded-lg tw:border tw:border-transparent hover:tw:border-gray-600 tw:transition-colors"
                                >
                                    <div className="tw:flex tw:items-center tw:gap-3">
                                        <RadioGroupItem value={option.id} id={option.id} />
                                        <IconComponent className="tw:flex-shrink-0 tw:w-6 tw:h-6" />
                                    </div>

                                    <div className="tw:flex-1 tw:min-w-0">
                                        <label htmlFor={option.id} className="tw:cursor-pointer tw:block">
                                            <h3 className="tw:!text-sm tw:!font-semibold tw:text-white tw:mb-2">
                                                {option.title}
                                            </h3>

                                            <p className="tw:text-sm tw:!font-normal tw:!m-0.5">{option.description}</p>

                                            <p className="tw:text-sm tw:!font-normal tw:!m-0.5">
                                                {option.additionalText}
                                            </p>

                                            <p className="tw:text-sm tw:!font-normal tw:text-[#899CBD]">
                                                {option.example}
                                            </p>
                                        </label>
                                    </div>
                                </div>
                            )
                        })}
                    </RadioGroup>
                </div>
            </div>
            <div className="tw:border-l tw:px-10 tw:py-6 tw:w-fit tw:flex-none tw:ml-auto tw:h-full">
                <Funnel availableStages={availableStages} audienceSelections={{}} />
            </div>
        </div>
    )
}

export default LocationTargeting
